import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";

export type MascotMood =
  | "idle" // normal friendly blinking
  | "talking" // mouth/soundwave audio reactive
  | "thinking" // scratching head, eyes looking up
  | "error" // X_X red warning glitch
  | "coffee" // holding cute steaming coffee cup
  | "pointing"; // pointing at data card

export type MascotColorway =
  | "ceramic-white" // Industrial Ceramic / Apple & Teenage Engineering (RECOMMENDED)
  | "cyber-cyan" // Brand Aligned Ice Cyan / Neo Tech
  | "retro-cream" // Retro Computer Beige / Braun & GameBoy Classic
  | "obsidian"; // Classic Dark Slate

interface ZuraCubeBotProps {
  mood?: MascotMood;
  colorway?: MascotColorway;
  size?: number; // default 380px
  floating?: boolean; // gentle levitation
  showBadge?: boolean;
}

export const ZuraCubeBot: React.FC<ZuraCubeBotProps> = ({
  mood = "idle",
  colorway = "ceramic-white",
  size = 380,
  floating = true,
  showBadge = true,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Floating physics
  const floatY = floating ? Math.sin(frame / 18) * 8 : 0;
  const floatRotate = floating ? Math.sin(frame / 28) * 2.2 : 0;

  // Eye blinking rhythm
  const blinkCycle = frame % 75;
  const isBlinking = blinkCycle > 70 && blinkCycle <= 74;

  // Ear antenna spring bob
  const earBob = Math.sin(frame / 14) * 3;

  // Talking mouth animation cycle
  const talkCycle = Math.sin(frame * 0.45);

  // PALETTE CONFIG BASED ON COLORWAY
  const getTheme = () => {
    switch (colorway) {
      case "ceramic-white":
        return {
          bodyBg: "#FFFFFF",
          bodyBorder: "#18181B",
          shadowColor: "#18181B",
          earBg: "#F4F4F5",
          earAccent: "#00F0FF",
          antennaStem: "#E4E4E7",
          antennaBall: "#00F0FF",
          visorBg: "#09090B",
          visorBorder: "#27272A",
          eyeColor: "#00F0FF",
          glowColor: "rgba(0, 240, 255, 0.9)",
          handBg: "#FFFFFF",
          handCenter: "#18181B",
          tagColor: "#00F0FF",
          coffeeBg: "#EA580C",
        };
      case "cyber-cyan":
        return {
          bodyBg: "#E0F2FE", // Soft icy cyan tint
          bodyBorder: "#0C4A6E",
          shadowColor: "#0C4A6E",
          earBg: "#BAE6FD",
          earAccent: "#0284C7",
          antennaStem: "#BAE6FD",
          antennaBall: "#0284C7",
          visorBg: "#082F49",
          visorBorder: "#0284C7",
          eyeColor: "#38BDF8",
          glowColor: "rgba(56, 189, 248, 0.95)",
          handBg: "#F0F9FF",
          handCenter: "#0C4A6E",
          tagColor: "#38BDF8",
          coffeeBg: "#0284C7",
        };
      case "retro-cream":
        return {
          bodyBg: "#F5EBE1", // Retro warm beige / GameBoy classic
          bodyBorder: "#292524",
          shadowColor: "#292524",
          earBg: "#E7D8C9",
          earAccent: "#EA580C", // Industrial safety orange
          antennaStem: "#D6C7B2",
          antennaBall: "#EA580C",
          visorBg: "#1C1917",
          visorBorder: "#44403C",
          eyeColor: "#00F0FF",
          glowColor: "rgba(0, 240, 255, 0.9)",
          handBg: "#FDFBF7",
          handCenter: "#292524",
          tagColor: "#EA580C",
          coffeeBg: "#EA580C",
        };
      case "obsidian":
      default:
        return {
          bodyBg: "#18181B",
          bodyBorder: "#09090B",
          shadowColor: "#09090B",
          earBg: "#27272A",
          earAccent: "#00F0FF",
          antennaStem: "#27272A",
          antennaBall: "#00F0FF",
          visorBg: "#09090B",
          visorBorder: "#27272A",
          eyeColor: "#00F0FF",
          glowColor: "rgba(0, 240, 255, 0.85)",
          handBg: "#F4F1EA",
          handCenter: "#18181B",
          tagColor: "#00F0FF",
          coffeeBg: "#EA580C",
        };
    }
  };

  const theme = getTheme();
  const eyeColor = mood === "error" ? "#EF4444" : theme.eyeColor;
  const glowColor =
    mood === "error" ? "rgba(239, 68, 68, 0.9)" : theme.glowColor;

  return (
    <div
      style={{
        width: size,
        height: size * 1.08,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        transform: `translateY(${floatY}px) rotate(${floatRotate}deg)`,
        transformOrigin: "center bottom",
      }}
    >
      {/* 1. TOP DUAL SPRING ANTENNAS */}
      <div
        style={{
          position: "absolute",
          top: -22,
          display: "flex",
          gap: 70,
          zIndex: 5,
        }}
      >
        {/* Left Antenna */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            transform: `rotate(-12deg) translateY(${earBob * 0.5}px)`,
          }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: mood === "error" ? "#EF4444" : theme.antennaBall,
              boxShadow: `0 0 14px ${
                mood === "error" ? "#EF4444" : theme.antennaBall
              }`,
              border: `2.5px solid ${theme.bodyBorder}`,
            }}
          />
          <div
            style={{
              width: 5,
              height: 22,
              backgroundColor: theme.antennaStem,
              border: `2px solid ${theme.bodyBorder}`,
              borderRadius: 3,
            }}
          />
        </div>

        {/* Right Antenna */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            transform: `rotate(12deg) translateY(${-earBob * 0.5}px)`,
          }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: mood === "error" ? "#EF4444" : theme.antennaBall,
              boxShadow: `0 0 14px ${
                mood === "error" ? "#EF4444" : theme.antennaBall
              }`,
              border: `2.5px solid ${theme.bodyBorder}`,
            }}
          />
          <div
            style={{
              width: 5,
              height: 22,
              backgroundColor: theme.antennaStem,
              border: `2px solid ${theme.bodyBorder}`,
              borderRadius: 3,
            }}
          />
        </div>
      </div>

      {/* 2. MAIN BENTO CUBE CHASSIS */}
      <div
        style={{
          width: size * 0.78,
          height: size * 0.72,
          backgroundColor: theme.bodyBg,
          borderRadius: 44,
          border: `4px solid ${theme.bodyBorder}`,
          boxShadow: `0 18px 36px rgba(0,0,0,0.2), 8px 12px 0px ${theme.shadowColor}`,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
          boxSizing: "border-box",
        }}
      >
        {/* Tech Corner Screws/Rivets */}
        <div
          style={{
            position: "absolute",
            top: 14,
            right: 18,
            display: "flex",
            gap: 4,
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor:
                colorway === "ceramic-white" ? "#D4D4D8" : "#3F3F46",
            }}
          />
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor:
                colorway === "ceramic-white" ? "#D4D4D8" : "#3F3F46",
            }}
          />
        </div>

        {/* Brand Badge Bottom-Left: Mini {Z} */}
        {showBadge && (
          <div
            style={{
              position: "absolute",
              bottom: 10,
              left: 20,
              display: "flex",
              alignItems: "center",
              gap: 4,
              opacity: 0.9,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontFamily: "monospace",
                fontWeight: 900,
                color: theme.tagColor,
                letterSpacing: 1,
              }}
            >
              &#123;Z&#125;
            </span>
            <span
              style={{
                fontSize: 9,
                fontFamily: "monospace",
                fontWeight: 800,
                color: colorway === "ceramic-white" ? "#71717A" : "#A1A1AA",
                letterSpacing: 0.5,
              }}
            >
              SYS.BOT
            </span>
          </div>
        )}

        {/* 3. GLOSSY OLED VISOR SCREEN */}
        <div
          style={{
            width: "100%",
            height: "82%",
            backgroundColor: theme.visorBg,
            borderRadius: 32,
            border: `2px solid ${theme.visorBorder}`,
            boxShadow:
              mood === "error"
                ? "inset 0 0 24px rgba(239, 68, 68, 0.4)"
                : "inset 0 0 24px rgba(0, 240, 255, 0.25)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Glass glare */}
          <div
            style={{
              position: "absolute",
              top: -40,
              left: -30,
              width: 120,
              height: 240,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              transform: "rotate(35deg)",
              pointerEvents: "none",
            }}
          />

          {/* VISOR EYES / EXPRESSIONS */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 32,
              width: "100%",
            }}
          >
            {/* --- EXPRESSION: ERROR (X _ X) --- */}
            {mood === "error" && (
              <>
                <svg width="44" height="44" viewBox="0 0 44 44">
                  <line
                    x1="8"
                    y1="8"
                    x2="36"
                    y2="36"
                    stroke="#EF4444"
                    strokeWidth="7"
                    strokeLinecap="round"
                  />
                  <line
                    x1="36"
                    y1="8"
                    x2="8"
                    y2="36"
                    stroke="#EF4444"
                    strokeWidth="7"
                    strokeLinecap="round"
                  />
                </svg>
                <svg width="44" height="44" viewBox="0 0 44 44">
                  <line
                    x1="8"
                    y1="8"
                    x2="36"
                    y2="36"
                    stroke="#EF4444"
                    strokeWidth="7"
                    strokeLinecap="round"
                  />
                  <line
                    x1="36"
                    y1="8"
                    x2="8"
                    y2="36"
                    stroke="#EF4444"
                    strokeWidth="7"
                    strokeLinecap="round"
                  />
                </svg>
              </>
            )}

            {/* --- EXPRESSION: THINKING (LOOKING UP) --- */}
            {mood === "thinking" && (
              <>
                <div
                  style={{
                    width: 38,
                    height: 44,
                    backgroundColor: eyeColor,
                    borderRadius: "20px 20px 10px 10px",
                    boxShadow: `0 0 16px ${glowColor}`,
                    transform: "translateY(-6px) rotate(-8deg)",
                  }}
                />
                <div
                  style={{
                    width: 38,
                    height: 44,
                    backgroundColor: eyeColor,
                    borderRadius: "20px 20px 10px 10px",
                    boxShadow: `0 0 16px ${glowColor}`,
                    transform: "translateY(-6px) rotate(8deg)",
                  }}
                />
              </>
            )}

            {/* --- EXPRESSION: IDLE / BLINKING ROUND GLOW EYES --- */}
            {(mood === "idle" || mood === "coffee" || mood === "pointing") && (
              <>
                <div
                  style={{
                    width: 36,
                    height: isBlinking ? 6 : 46,
                    backgroundColor: eyeColor,
                    borderRadius: isBlinking ? 4 : 18,
                    boxShadow: `0 0 18px ${glowColor}`,
                    transition: "height 0.08s ease",
                  }}
                />
                <div
                  style={{
                    width: 36,
                    height: isBlinking ? 6 : 46,
                    backgroundColor: eyeColor,
                    borderRadius: isBlinking ? 4 : 18,
                    boxShadow: `0 0 18px ${glowColor}`,
                    transition: "height 0.08s ease",
                  }}
                />
              </>
            )}

            {/* --- EXPRESSION: TALKING (ANIMATED EQUALIZER) --- */}
            {mood === "talking" && (
              <>
                <div
                  style={{
                    width: 34,
                    height: 42,
                    backgroundColor: eyeColor,
                    borderRadius: 16,
                    boxShadow: `0 0 16px ${glowColor}`,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    height: 24,
                  }}
                >
                  <div
                    style={{
                      width: 4,
                      height: 12 + talkCycle * 8,
                      backgroundColor: eyeColor,
                      borderRadius: 2,
                    }}
                  />
                  <div
                    style={{
                      width: 4,
                      height: 22 + talkCycle * 10,
                      backgroundColor: eyeColor,
                      borderRadius: 2,
                    }}
                  />
                  <div
                    style={{
                      width: 4,
                      height: 12 + talkCycle * 8,
                      backgroundColor: eyeColor,
                      borderRadius: 2,
                    }}
                  />
                </div>
                <div
                  style={{
                    width: 34,
                    height: 42,
                    backgroundColor: eyeColor,
                    borderRadius: 16,
                    boxShadow: `0 0 16px ${glowColor}`,
                  }}
                />
              </>
            )}
          </div>

          {/* Cute digital cheek blush dots */}
          {mood !== "error" && (
            <div
              style={{
                position: "absolute",
                bottom: 12,
                display: "flex",
                gap: 90,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 4,
                  backgroundColor: "rgba(0, 240, 255, 0.4)",
                  borderRadius: 2,
                }}
              />
              <div
                style={{
                  width: 8,
                  height: 4,
                  backgroundColor: "rgba(0, 240, 255, 0.4)",
                  borderRadius: 2,
                }}
              />
            </div>
          )}
        </div>

        {/* 4. SIDE HEADPHONE EARCUPS */}
        {/* Left Earcup */}
        <div
          style={{
            position: "absolute",
            left: -20,
            width: 22,
            height: 64,
            backgroundColor: theme.earBg,
            border: `3.5px solid ${theme.bodyBorder}`,
            borderRadius: "14px 4px 4px 14px",
            boxShadow: `-3px 4px 0px ${theme.shadowColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 4,
              height: 28,
              backgroundColor: mood === "error" ? "#EF4444" : theme.earAccent,
              borderRadius: 2,
            }}
          />
        </div>

        {/* Right Earcup */}
        <div
          style={{
            position: "absolute",
            right: -20,
            width: 22,
            height: 64,
            backgroundColor: theme.earBg,
            border: `3.5px solid ${theme.bodyBorder}`,
            borderRadius: "4px 14px 14px 4px",
            boxShadow: `3px 4px 0px ${theme.shadowColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 4,
              height: 28,
              backgroundColor: mood === "error" ? "#EF4444" : theme.earAccent,
              borderRadius: 2,
            }}
          />
        </div>
      </div>

      {/* 5. FLOATING MAGNETIC HANDS */}
      {/* Hand Left */}
      <div
        style={{
          position: "absolute",
          left: 6,
          bottom: mood === "coffee" ? 54 : 36,
          width: 44,
          height: 44,
          backgroundColor: theme.handBg,
          border: `3.5px solid ${theme.bodyBorder}`,
          borderRadius: "50%",
          boxShadow: `4px 4px 0px ${theme.shadowColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform:
            mood === "coffee"
              ? "rotate(24deg) translateY(-8px)"
              : `translateY(${Math.sin(frame / 20) * 4}px)`,
        }}
      >
        <div
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: theme.handCenter,
          }}
        />
      </div>

      {/* Hand Right */}
      <div
        style={{
          position: "absolute",
          right: 6,
          bottom: mood === "pointing" ? 64 : 36,
          width: 44,
          height: 44,
          backgroundColor: theme.handBg,
          border: `3.5px solid ${theme.bodyBorder}`,
          borderRadius: "50%",
          boxShadow: `4px 4px 0px ${theme.shadowColor}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform:
            mood === "pointing"
              ? "rotate(-35deg) translate(-4px, -14px)"
              : `translateY(${Math.cos(frame / 20) * 4}px)`,
        }}
      >
        {mood === "pointing" ? (
          <div
            style={{
              width: 8,
              height: 20,
              backgroundColor: theme.handCenter,
              borderRadius: 4,
              transform: "translateY(-4px)",
            }}
          />
        ) : (
          <div
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: theme.handCenter,
            }}
          />
        )}
      </div>

      {/* Prop: Steaming Coffee Mug if mood == 'coffee' */}
      {mood === "coffee" && (
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: 28,
            backgroundColor: theme.coffeeBg,
            border: `3px solid ${theme.bodyBorder}`,
            borderRadius: "4px 4px 10px 10px",
            width: 32,
            height: 38,
            boxShadow: `3px 3px 0px ${theme.shadowColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transform: `translateY(${Math.sin(frame / 15) * 3}px)`,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontFamily: "monospace",
              fontWeight: 900,
              color: "#FFFFFF",
            }}
          >
            &#123;Z&#125;
          </span>
          <div
            style={{
              position: "absolute",
              top: -12,
              width: 3,
              height: 8,
              backgroundColor: "rgba(28, 25, 23, 0.4)",
              borderRadius: 2,
              transform: `translateY(${Math.sin(frame / 10) * 3}px)`,
            }}
          />
        </div>
      )}

      {/* 6. SHADOW AT BASE */}
      <div
        style={{
          width: size * 0.54,
          height: 16,
          backgroundColor: "rgba(28, 25, 23, 0.18)",
          borderRadius: "50%",
          filter: "blur(4px)",
          marginTop: 18,
          transform: `scale(${1 - Math.abs(floatY) * 0.03})`,
        }}
      />
    </div>
  );
};
